import type {Comment, Thread} from '@github-ui/diff-lines/conversation-types'
import {getComments, getInitialComment, getLatestComment, getLatestReply} from './thread-comment-accessors'
import {isCopilot} from '@github-ui/assignees/copilot-user'

export const ThreadGroupVariant = {
  /** Someone other than the current viewer has commented and nobody has replied yet */
  Commented: 'commented',
  /** Someone other than the current viewer has replied to a comment */
  Replied: 'replied',
  /** Someone has mentioned the current viewer in the latest comment */
  Mentioned: 'mentioned',
  /** The thread is resolved */
  Resolved: 'resolved',
  /** The current viewer has commented on the thread */
  YouCommented: 'you-commented',
  /** The current viewer has replied to a comment in the thread */
  YouReplied: 'you-replied',
} as const

export type ThreadGroupVariant = (typeof ThreadGroupVariant)[keyof typeof ThreadGroupVariant]
export type UnresolvedThreadGroupVariant = Exclude<ThreadGroupVariant, typeof ThreadGroupVariant.Resolved>

export const ThreadGroupVariantLabels: Record<ThreadGroupVariant, string> = {
  [ThreadGroupVariant.Commented]: 'commented',
  [ThreadGroupVariant.Replied]: 'replied to a comment',
  [ThreadGroupVariant.Mentioned]: 'mentioned you',
  [ThreadGroupVariant.Resolved]: 'commented',
  [ThreadGroupVariant.YouCommented]: 'commented',
  [ThreadGroupVariant.YouReplied]: 'replied',
}

/**
 * Returns true when a comment's body `@`-mentions the given login.
 * Matches the raw body text on a username token boundary (case-insensitive), so
 * `@octocat` matches but `@octocat-bot` or an email-style `foo@octocat` does not.
 */
export function bodyMentionsViewer(comment: Comment | undefined, viewerLogin: string | undefined): boolean {
  if (!comment?.body || !viewerLogin) return false

  const escapedLogin = viewerLogin.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const mentionPattern = new RegExp(`(^|[^a-z0-9-])@${escapedLogin}(?![a-z0-9-])`, 'i')
  return mentionPattern.test(comment.body)
}

/**
 * Classifies a thread into exactly one {@link UnresolvedThreadGroupVariant}.
 *
 * Unresolved threads are evaluated in precedence order (first match wins):
 *  1. `mentioned` — the latest comment mentions the viewer
 *  2. `replied` — someone replied to an existing comment
 *  3. `commented` — everything else
 *
 * `viewerLogin` is `undefined` when logged out, in which case authorship and mention
 * checks are skipped and unresolved threads fall through to `commented`.
 */
export function classifyUnresolvedThread(
  thread: Thread,
  viewerLogin: string | undefined,
): UnresolvedThreadGroupVariant {
  const comments = getComments(thread)
  const initial = comments[0]
  const latest = comments[comments.length - 1]

  if (!initial || !latest) return ThreadGroupVariant.Commented

  const hasReply = comments.length > 1
  const viewerAuthoredLatest = latest.viewerDidAuthor ?? false

  // 1. The freshest activity is a comment that mentions the viewer.
  if (!viewerAuthoredLatest && bodyMentionsViewer(latest, viewerLogin)) {
    return ThreadGroupVariant.Mentioned
  }

  // 2. Someone replied to a comment the viewer started.
  if (hasReply) return viewerAuthoredLatest ? ThreadGroupVariant.YouReplied : ThreadGroupVariant.Replied

  // 3. Unreplied comment
  return viewerAuthoredLatest ? ThreadGroupVariant.YouCommented : ThreadGroupVariant.Commented
}

export const GHOST_LOGIN = 'ghost'

interface ThreadAuthor {
  login: string
  linkable: boolean
}

/**
 * Returns the login to attribute a thread's headline to for the given variant,
 * falling back to the ghost user when the thread or comment author no longer exists.
 */
export function getThreadAuthor(thread: Thread | null | undefined, variant: ThreadGroupVariant): ThreadAuthor {
  if (!thread) return {login: GHOST_LOGIN, linkable: false}

  if (variant === ThreadGroupVariant.YouReplied || variant === ThreadGroupVariant.YouCommented)
    return {login: 'You', linkable: false}

  const login = getVariantComment(thread, variant)?.author?.login

  if (login === undefined) return {login: GHOST_LOGIN, linkable: true}

  if (isCopilot(login) || login.endsWith('[bot]')) return {login, linkable: false}

  return {login, linkable: true}
}

/**
 * Extracts the single comment whose author becomes the headline's "X" for the given variant.
 */
export function getVariantComment(thread: Thread, variant: ThreadGroupVariant): Comment | undefined {
  switch (variant) {
    case ThreadGroupVariant.Replied:
    case ThreadGroupVariant.YouReplied:
      return getLatestReply(thread) ?? getLatestComment(thread)
    case ThreadGroupVariant.Mentioned:
      // "X" is whoever wrote the comment that mentioned the viewer.
      // TODO: detect the mentioning comment explicitly; fall back to the latest comment for now.
      return getLatestComment(thread)
    case ThreadGroupVariant.Resolved:
    case ThreadGroupVariant.Commented:
    case ThreadGroupVariant.YouCommented:
    default:
      return getInitialComment(thread)
  }
}
